import React, {Fragment, useState, useEffect} from 'react';
import {createPortal} from 'react-dom';
import Corner from './corner.svg';
import {ActiveTimer} from './active-timer';
import {Link, Tag, Icons, Logo, Popup} from 'components';
import {printMain, useLocation, find, partial, config, shorten, useMedia, openPopup, useNamedSelector, useWindowSize, useUpdate} from 'helpers';
import {store} from 'store';
import {set} from 'store/settings';
import css from './topbar.module.scss';

export function Topbar() {
  const { user, subscription, offline, updateAvailable } = useNamedSelector('session.session');
  const { reading, updating } = useNamedSelector('server.server');
  return (
    <header className={css('topbar', 'popup-container')}>
      <div id="tabs" className={css.tabs} />
      <Link to="/">
        <Logo className={css('small-logo')} />
      </Link>
      <div id="toolbar" className={css.toolbar}>
        { Boolean(user) &&
          <Fragment>
            { updateAvailable &&
              <Tag className="bordered" icon="update" onClick={openPopup(UpdateTooltip)}>
                Update available
              </Tag>
            }
            <Tag to="/settings/membership/" className="orange">
              { subscription.premium ? 'Premium' : 'Get Premium' }
            </Tag>
            <span className={css.user}>
              { shorten(user.email, { chars: 30 }) }
            </span>
          </Fragment>
        }
      </div>
      <div className={css.menu}>
        <i />
        <Icons.CloudO Sub={offline ? Icons.Close : reading || updating ? Icons.Spinner : Icons.Check} />
        <Icons.Print onClick={printMain} />
        <ActiveTimer />
        <Icons.Menu onClick={openPopup(UserMenu)} />
      </div>
    </header>
  );
}

function UpdateTooltip() {
  return 'Please reload the web application to load the latest version.';
}

function UserMenu() {
  const isDesktop = useMedia('(min-width: 62em)');
  const { subscription } = useNamedSelector('session.session');
  const { theme } = useNamedSelector('settings.settings');
  return (
    <Popup.Menu>
      <Popup.Menu.Item icon={{ name: 'person', Sub: subscription.premium && Icons.Crown }} primary={subscription.premium} to="/settings/membership/">
        { subscription.premium ? 'Premium' : subscription.plan } account
      </Popup.Menu.Item>
      <Popup.Menu.Separator />
      { !isDesktop &&
        <Fragment>
          <Popup.Menu.Item icon="today" to="/today/">Today</Popup.Menu.Item>
          <Popup.Menu.Item icon="goal" to="/goals/">Goals</Popup.Menu.Item>
          <Popup.Menu.Item icon="clipboard" to="/activities/">Activities</Popup.Menu.Item>
          <Popup.Menu.Item icon="recurring" to="/routines/">Routines</Popup.Menu.Item>
          { config.sw &&
            <Popup.Menu.Item icon="habit" to="/habits/">Habits</Popup.Menu.Item>
          }
          <Popup.Menu.Item icon="diary" to="/diary/">Journal</Popup.Menu.Item>
          <Popup.Menu.Item icon="notepad" to="/notes/">Notes</Popup.Menu.Item>
        </Fragment>
      }
      <Popup.Menu.Item icon="gear-o" to="/settings/">Settings</Popup.Menu.Item>
      <Popup.Menu.Item icon="bulb" onClick={partial(set, { key: 'theme', value: toggleTheme })}>{ theme == 'light' ? 'Dark' : 'Light' } Mode</Popup.Menu.Item>
      <Popup.Menu.Item icon="export" to="/settings/export/">Export Data</Popup.Menu.Item>
      <Popup.Menu.Item icon="gift" to="/settings/whats-new/">What's New</Popup.Menu.Item>
      {(config.development || config.uat) && <Popup.Menu.Item to="/test/">Test</Popup.Menu.Item>}
      <Popup.Menu.Separator />
      <Popup.Menu.Item icon="logout" onClick={() => store.dispatch({ type: 'SESSION_LOGOUT' })} className="danger">Logout</Popup.Menu.Item>
    </Popup.Menu>
  );
}

function toggleTheme(value) {
  return value == 'light' ? 'dark' : 'light';
}

Topbar.Tabs = function Tabs({ children }) {
  const update = useUpdate();
  const [ refs ] = useState({
    tabs: window.document.createElement('div'),
    left: { current: null },
    right: { current: null },
  });

  useEffect(() => {
    update(refs.tabs = window.document.getElementById('tabs'));
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (find(refs.tabs.children, { className: 'active' })) {
        const offset = refs.tabs.getBoundingClientRect().left;
        const { left, right } = find(refs.tabs.children, { className: 'active' }).getBoundingClientRect();
        refs.right.current.style.left = right - offset + 'px';
        refs.left.current.style.left = left - offset + 'px';
      }
    }, 1);
  }, [ useLocation(), useWindowSize() ]);

  return createPortal((
    <Fragment>
      { children }
      <Corner className={css('last-corner')} />
      <Corner className={css('tab-left-corner')} ref={refs.left} />
      <Corner className={css('tab-right-corner')} ref={refs.right} />
    </Fragment>
  ), refs.tabs);
}

Topbar.Toolbar = function Toolbar({ children }) {
  const [ container, setContainer ] = useState(window.document.createElement('div'));
  useEffect(() => setContainer(window.document.getElementById('toolbar')), []);
  return createPortal(children, container);
}
